import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { SurfaceReportRepository } from "@partikelart/surface/data-access-core";
import type { SurfaceReport, SurfaceReportSummary, SurfaceReportWithSpecification } from "@partikelart/surface/domain";
import type {
	CreateSurfaceReportDto,
	SurfaceReportDto,
	SurfaceReportSummaryDto,
	SurfaceReportWithSpecificationDto,
	UpdateSurfaceReportDto,
} from "@partikelart/surface/dtos";
import { map, Observable } from "rxjs";
import { SURFACE_REPORTS_API_CONFIG } from "./surface-reports-api-config";

/** HTTP implementation of the surface report repository. */
@Injectable()
export class CloudSurfaceReportRepository implements SurfaceReportRepository {
	private readonly config = inject(SURFACE_REPORTS_API_CONFIG);
	private readonly http = inject(HttpClient);

	private get baseUrl(): string {
		return `${this.config.portalBackendUrl ?? "http://localhost:3000"}/api`;
	}

	getAll(params?: {
		siteId?: string;
		partId?: string;
		dateFrom?: string;
		dateTo?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ data: SurfaceReport[]; total: number }> {
		let httpParams = new HttpParams();
		if (params?.siteId) httpParams = httpParams.set("siteId", params.siteId);
		if (params?.partId) httpParams = httpParams.set("part_id", String(params.partId));
		if (params?.dateFrom) httpParams = httpParams.set("date_from", params.dateFrom);
		if (params?.dateTo) httpParams = httpParams.set("date_to", params.dateTo);
		if (params?.limit != null) httpParams = httpParams.set("limit", String(params.limit));
		if (params?.offset != null) httpParams = httpParams.set("offset", String(params.offset));
		return this.http
			.get<{ data: SurfaceReportDto[]; total: number }>(`${this.baseUrl}/surface-reports`, {
				params: httpParams,
			})
			.pipe(map((result) => ({ data: result.data.map(reviveDates), total: result.total })));
	}

	getSummaries(params?: {
		siteId?: string;
		partId?: string;
		dateFrom?: string;
		dateTo?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ data: SurfaceReportSummary[]; total: number }> {
		let httpParams = new HttpParams();
		if (params?.siteId) httpParams = httpParams.set("siteId", params.siteId);
		if (params?.partId) httpParams = httpParams.set("part_id", String(params.partId));
		if (params?.dateFrom) httpParams = httpParams.set("date_from", params.dateFrom);
		if (params?.dateTo) httpParams = httpParams.set("date_to", params.dateTo);
		if (params?.limit != null) httpParams = httpParams.set("limit", String(params.limit));
		if (params?.offset != null) httpParams = httpParams.set("offset", String(params.offset));
		return this.http
			.get<{ data: SurfaceReportSummaryDto[]; total: number }>(`${this.baseUrl}/surface-reports/summary`, {
				params: httpParams,
			})
			.pipe(map((result) => ({ data: result.data.map(reviveDates), total: result.total })));
	}

	getById(id: string): Observable<SurfaceReport | null> {
		return this.http
			.get<SurfaceReportDto>(`${this.baseUrl}/surface-reports/${id}`)
			.pipe(map((r) => (r ? reviveDates(r) : null)));
	}

	getSummaryById(id: string): Observable<SurfaceReportSummary | null> {
		return this.http
			.get<SurfaceReportSummaryDto>(`${this.baseUrl}/surface-reports/${id}/summary`)
			.pipe(map((r) => (r ? reviveDates(r) : null)));
	}

	getByIdWithSpecification(id: string): Observable<SurfaceReportWithSpecification | null> {
		return this.http
			.get<SurfaceReportWithSpecificationDto>(`${this.baseUrl}/surface-reports/${id}/detail`)
			.pipe(map((r) => (r ? reviveDates(r) : null)));
	}

	create(input: CreateSurfaceReportDto): Observable<SurfaceReport> {
		return this.http.post<SurfaceReportDto>(`${this.baseUrl}/surface-reports`, input).pipe(map(reviveDates));
	}

	update(id: string, changes: UpdateSurfaceReportDto): Observable<SurfaceReport> {
		return this.http.put<SurfaceReportDto>(`${this.baseUrl}/surface-reports/${id}`, changes).pipe(map(reviveDates));
	}

	delete(id: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/surface-reports/${id}`);
	}

	assignSpecification(id: string, specificationId: string): Observable<SurfaceReport> {
		return this.http
			.put<SurfaceReportDto>(`${this.baseUrl}/surface-reports/${id}/specification`, { specificationId })
			.pipe(map(reviveDates));
	}

	removeSpecification(id: string): Observable<SurfaceReport> {
		return this.http
			.delete<SurfaceReportDto>(`${this.baseUrl}/surface-reports/${id}/specification`)
			.pipe(map(reviveDates));
	}
}

/** Revive ISO date strings into Date objects for the domain's Date-typed fields. */
const DATE_FIELDS = ["dateOfArrival", "dateOfExtraction", "dateOfAnalysis", "createdAt", "updatedAt"] as const;

function reviveDates<T>(dto: T): T {
	const revived = { ...dto } as Record<string, unknown>;
	for (const field of DATE_FIELDS) {
		const value = revived[field];
		if (typeof value === "string") {
			revived[field] = new Date(value);
		}
	}
	return revived as T;
}
